import React from "react";

// icon
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CasinoOutlinedIcon from "@mui/icons-material/CasinoOutlined";
import TerminalOutlinedIcon from "@mui/icons-material/TerminalOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import GifBoxOutlinedIcon from "@mui/icons-material/GifBoxOutlined";
import AutoAwesomeMosaicOutlinedIcon from "@mui/icons-material/AutoAwesomeMosaicOutlined";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";

export const CATEGORIES = [
    {id: 'dev', label: '개발 도구', code: 'DEV'},
    {id: 'convert', label: '변환 · 계산', code: 'CONVERT'},
    {id: 'media', label: '이미지 · QR', code: 'MEDIA'},
    {id: 'daily', label: '시간 · 일상', code: 'DAILY'},
] as const;

export type CategoryId = typeof CATEGORIES[number]['id'];

export const TAGS = ['개발', '변환', '이미지', '일상'];

export interface ToolItem {
    title: string;
    desc: string;
    to: string;
    icon: React.ReactNode;
    category: CategoryId;
    tags: string[];
    // 라우트에 그대로 꽂는 지연 로딩 컴포넌트. 도구 코드가 첫 로드 번들에 섞이지 않게 한다.
    component: React.LazyExoticComponent<React.ComponentType>;
}

// 저항 색띠 아이콘 (인라인 SVG)
function ResistorIcon(): JSX.Element {
    return (
        <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M2 12h4"/>
            <path d="M18 12h4"/>
            <rect x="6" y="7" width="12" height="10" rx="2"/>
            <path d="M9.5 7v10"/>
            <path d="M12 7v10"/>
            <path d="M14.5 7v10"/>
        </svg>
    );
}

const iconSx = {fontSize: 20} as const;

// 도구 목록의 단일 출처. 메인 화면 카드 목록과 라우트 둘 다 이 배열에서 만든다.
// 순서는 메인 화면 카테고리 섹션 안 카드 배치 순서이기도 해서 임의로 바꾸지 않는다.
export const tools: ToolItem[] = [
    {
        title: "글자수 세기",
        desc: "공백 포함·미포함 글자 수 계산",
        to: "/wordcount",
        icon: <DescriptionOutlinedIcon sx={iconSx}/>,
        category: 'daily',
        tags: ['일상'],
        component: React.lazy(() => import("../Page/WordCount/WordCountBase")),
    },
    {
        title: "랜덤 선택",
        desc: "항목 저장·불러오기, 카운트다운 추첨",
        to: "/random",
        icon: <CasinoOutlinedIcon sx={iconSx}/>,
        category: 'daily',
        tags: ['일상'],
        component: React.lazy(() => import("../Page/RandomSelectPage")),
    },
    {
        title: "저항 색띠 계산",
        desc: "색띠 → 저항값 계산",
        to: "/resistance",
        icon: <ResistorIcon/>,
        category: 'convert',
        tags: ['변환'],
        component: React.lazy(() => import("../Page/ResistanceCalculatorPage")),
    },
    {
        title: "정규식 생성",
        desc: "문자열 → 정규식 변환·테스트",
        to: "/regex",
        icon: <TerminalOutlinedIcon sx={iconSx}/>,
        category: 'dev',
        tags: ['개발'],
        component: React.lazy(() => import("../Page/RegexCreatePage")),
    },
    {
        title: "QR코드 생성",
        desc: "Wi-Fi·명함 템플릿, 프리셋·로고 커스텀 QR",
        to: "/qrcode",
        icon: <QrCode2OutlinedIcon sx={iconSx}/>,
        category: 'media',
        tags: ['이미지', '일상'],
        component: React.lazy(() => import("../Page/QRCodeCreatePage")),
    },
    {
        title: "진법 변환",
        desc: "2·8·10·16진수 및 사용자 지정 진수 변환",
        to: "/base",
        icon: <CalculateOutlinedIcon sx={iconSx}/>,
        category: 'convert',
        tags: ['변환', '개발'],
        component: React.lazy(() => import("../Page/BaseConverterPage")),
    },
    {
        title: "단위 변환",
        desc: "길이·무게·온도·데이터 단위 변환",
        to: "/unit",
        icon: <StraightenOutlinedIcon sx={iconSx}/>,
        category: 'convert',
        tags: ['변환', '일상'],
        component: React.lazy(() => import("../Page/UnitConverterPage")),
    },
    {
        title: "색상 변환",
        desc: "HEX·RGB·HSL 상호 변환 및 미리보기",
        to: "/color",
        icon: <PaletteOutlinedIcon sx={iconSx}/>,
        category: 'convert',
        tags: ['변환', '개발', '이미지'],
        component: React.lazy(() => import("../Page/ColorConverterPage")),
    },
    {
        title: "Base64 변환",
        desc: "텍스트 ↔ Base64 인코딩·디코딩",
        to: "/base64",
        icon: <CodeOutlinedIcon sx={iconSx}/>,
        category: 'dev',
        tags: ['개발'],
        component: React.lazy(() => import("../Page/Base64Page")),
    },
    {
        title: "JWT 변환",
        desc: "토큰 디코딩·서명 검증·생성",
        to: "/jwt",
        icon: <KeyOutlinedIcon sx={iconSx}/>,
        category: 'dev',
        tags: ['개발'],
        component: React.lazy(() => import("../Page/JwtPage")),
    },
    {
        title: "타임스탬프 변환",
        desc: "Unix timestamp ↔ 날짜·시간 변환",
        to: "/timestamp",
        icon: <ScheduleOutlinedIcon sx={iconSx}/>,
        category: 'dev',
        tags: ['개발', '변환'],
        component: React.lazy(() => import("../Page/TimestampPage")),
    },
    {
        title: "JSON 포맷터",
        desc: "JSON 검증·포맷팅·압축",
        to: "/json",
        icon: <DataObjectOutlinedIcon sx={iconSx}/>,
        category: 'dev',
        tags: ['개발'],
        component: React.lazy(() => import("../Page/JsonFormatterPage")),
    },
    {
        title: "D-day 계산",
        desc: "디데이 및 두 날짜 차이 계산",
        to: "/dday",
        icon: <EventOutlinedIcon sx={iconSx}/>,
        category: 'daily',
        tags: ['일상'],
        component: React.lazy(() => import("../Page/DdayPage")),
    },
    {
        title: "카운트다운",
        desc: "마일스톤·목표 일시 전광판 타이머",
        to: "/countdown",
        icon: <TimerOutlinedIcon sx={iconSx}/>,
        category: 'daily',
        tags: ['일상'],
        component: React.lazy(() => import("../Page/CountdownPage")),
    },
    {
        title: "GIF 만들기",
        desc: "영상 구간을 골라 GIF 생성·장면 캡처",
        to: "/gif",
        icon: <GifBoxOutlinedIcon sx={iconSx}/>,
        category: 'media',
        tags: ['이미지'],
        component: React.lazy(() => import("../Page/GifMakerPage")),
    },
    {
        title: "이미지 콜라주",
        desc: "여러 이미지를 그리드로 합쳐 한 장으로 저장",
        to: "/collage",
        icon: <AutoAwesomeMosaicOutlinedIcon sx={iconSx}/>,
        category: 'media',
        tags: ['이미지'],
        component: React.lazy(() => import("../Page/ImageCollagePage")),
    },
    {
        title: "이미지 편집기",
        desc: "모자이크·사각형·화살표·선·텍스트 이미지 편집",
        to: "/imageedit",
        icon: <BrushOutlinedIcon sx={iconSx}/>,
        category: 'media',
        tags: ['이미지'],
        component: React.lazy(() => import("../Page/ImageEditorPage")),
    },
];

// pathname이 도구 경로와 정확히 같거나 그 하위 경로(예: /wordcount/advanced)면 해당 도구를 돌려준다.
export function findToolByPath(pathname: string): ToolItem | undefined {
    return tools.find((tool) => pathname === tool.to || pathname.startsWith(`${tool.to}/`));
}
